import { useState, useEffect } from 'react'
import { Modal, Button, Checkbox, Alert, Typography, Space, Divider, App } from 'antd'
import { FileTextOutlined, DownloadOutlined, CheckCircleOutlined } from '@ant-design/icons'
import type { CheckboxChangeEvent } from 'antd/es/checkbox'
import { authService } from '../../services/authService'

const { Paragraph, Text, Title } = Typography

/**
 * Current EULA version - increment this when EULA terms change
 * Frontend compares user's accepted version against this value
 */
export const CURRENT_EULA_VERSION = 1

/**
 * EULA plain text for download
 */
const EULA_PLAIN_TEXT = `Rocket Pricing Demonstration Software License Agreement

LICENSE. Rocket Pricing, LLC ("Rocket Pricing") grants You the right to view, interact with, access and potentially install, the Rocket Pricing demonstration software ("Demo") offering with limited features to evaluate or review the capabilities of Demo software. The Demo software shall not be, in any case, directly or indirectly assembled as a part in any production of Yours, as it is solely developed to serve demonstration purposes and has no direct function.

Demo software from Rocket Pricing is not a finished product, nor should any Demo software be considered supported or maintained by Rocket Pricing, or any other party. Certain Demo software, in part or in whole, may be included under separate agreement, under a separate accompanying end user license agreement, or separate statement of work, in which case the terms and conditions of such agreement shall apply to that Demo software.

By using Demo software, You are agreeing to be bound by the terms and conditions of this agreement. Do not use Demo software until You have read and agreed to the following terms and conditions. The use of the Demo software implies automatically the acceptance of the following terms and conditions.

DEMO SOFTWARE STATUS. Demo software offers limited features allowing You only to evaluate and review the software. Demo software is not an end product (or finished product), nor is it intended or authorized by Rocket Pricing to be integrated into end products. You are not authorized to use the Demo software in any production system, proprietary system, or derivative works, and may not be offered for sale or lease, or sold, leased or otherwise distributed.

OWNERSHIP AND COPYRIGHT. Title to the Demo software, related documentation and all copies thereof remain with Rocket Pricing and/or its licensors. You may not remove the copyrights notices from the Demo software. You may not copy the software for back-up or archival purposes. You agree to prevent any unauthorized copying of the Demo software and related documentation.

RESTRICTIONS. You may not sell, assign, sublicense or otherwise distribute the Demo software for commercial purposes, in whole or in part, or use Demo software in a production system. Except as provided in this Agreement or in the Demo software's documentation, You may not reproduce the Demo software or related documentation, or modify, reverse engineer, de-compile or disassemble the Demo software, in whole or in part.

NO WARRANTY. THE DEMO SOFTWARE IS PROVIDED "AS IS" AND "WITH ALL FAULTS;" WITHOUT WARRANTY OF ANY KIND, EXPRESSED OR IMPLIED. ROCKET PRICING EXPRESSLY DISCLAIMS ALL WARRANTIES, EXPRESSED, IMPLIED OR OTHERWISE, INCLUDING WITHOUT LIMITATION, WARRANTY OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NON-INFRINGEMENT OF INTELLECTUAL PROPERTY RIGHTS. Rocket Pricing does not warrant that the use in whole or in part of the Demo software will be interrupted or error free, will meet any specific requirements, or will operate with the combination of hardware and software selected by You. You are responsible for determining whether the Demo software will be suitable for your intended use or application or will achieve your intended results. Demo software is not designed nor authorized for use in business or safety critical applications such as financial services, life supporting devices or systems, or for military or nuclear applications. Rocket Pricing expressly disclaims any responsibility for such usage which shall be made at Your sole risk, even if Rocket Pricing has been informed in writing of such usage. You shall indemnify Rocket Pricing, its officers, employees and affiliates against all claims arising directly or indirectly from Your incorporation of the Demo software in any application or system where failure of the Demo software could lead to financial loss, death or personal injury.

ROCKET PRICING shall not have any liability in case of damages, losses, claims or actions anyhow caused from combination of the Demo software with another product, software or device.

Rocket Pricing has not authorized anyone to make any representation or warranty for the Demo software, and any technical, applications or design information or advice, quality characterization, reliability data or other services provided by Rocket Pricing shall not constitute any representation or warranty by Rocket Pricing or alter this disclaimer or warranty, and no additional obligations or liabilities shall arise from Rocket Pricing's providing such information or services. Rocket Pricing does not assume or authorize any other person to assume for it any other liability in connection with its Demo software.

All other warranties, conditions or other terms implied by law are excluded to the fullest extent permitted by law.

LIMITATION OF LIABILITIES. IN NO EVENT WILL ROCKET PRICING OR ITS LICENSORS BE LIABLE TO YOU OR ANY THIRD PARTY FOR ANY INDIRECT, SPECIAL, CONSEQUENTIAL, INCIDENTAL, PUNITIVE DAMAGES OR OTHER DAMAGES (INCLUDING BUT NOT LIMITED TO, THE COST OF LABOR, REQUALIFICATION, DELAY, LOSS OF PROFITS, LOSS OF REVENUES, LOSS OF DATA, COSTS OF PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES OR THE LIKE) WHETHER BASED ON CONTRACT, TORT, OR ANY OTHER LEGAL THEORY, RELATING TO OR IN CONNECTION WITH THE DEMO PRODUCT, THE DOCUMENTATION OR THIS AGREEMENT, EVEN IF ROCKET PRICING HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.

In no event shall Rocket Pricing's aggregate liability to You or any third party under this agreement for any cause or action, whether based on contract, tort, or any other legal theory, relating to or in connection with the Demo software, the documentation or this agreement, shall exceed the purchase price paid for the Demo software, if any.

TERMINATION. Rocket Pricing may terminate this license at any time, if in its sole discretion You are in breach of any of its terms and conditions. Upon termination, Your access to any Demo software will be revoked, or You will immediately destroy or return all copies of the demo software and documentation to Rocket Pricing.

APPLICABLE LAW AND JURISDICTION. In case of dispute and in the absence of an amicable settlement, the only competent jurisdiction shall be the state or federal courts located in Cuyahoga County, Ohio, United States of America. The applicable law shall be the law of the State of Ohio.

SEVERABILITY. If any provision of this agreement is or becomes, at any time or for any reason, unenforceable or invalid, no other provision of this agreement shall be affected thereby, and the remaining provisions of this agreement shall continue with the same force and effect as if such unenforceable or invalid provisions had not been inserted in this Agreement.

WAIVER. The waiver by either party of any breach of any provisions of this Agreement shall not be construed as a waiver of any other or a subsequent breach of the same or a different provision.

RELATIONSHIP OF THE PARTIES. Nothing in this Agreement shall create, or be deemed to create, a partnership or the relationship of principal and agent or employer and employee between the Parties. Neither Party has the authority or power to bind, to contract in the name of or to create a liability for the other in any way or for any purpose.`

/**
 * EULA content as JSX for proper formatting
 */
const EulaContent: React.FC = () => (
  <Typography>
    <Title level={4} style={{ textAlign: 'center', marginBottom: 24 }}>
      Rocket Pricing Demonstration Software License Agreement
    </Title>

    <Paragraph>
      <Text strong>LICENSE.</Text> Rocket Pricing, LLC ("Rocket Pricing") grants You the right to view, interact with, access and potentially install, the Rocket Pricing demonstration software ("Demo") offering with limited features to evaluate or review the capabilities of Demo software. The Demo software shall not be, in any case, directly or indirectly assembled as a part in any production of Yours, as it is solely developed to serve demonstration purposes and has no direct function.
    </Paragraph>

    <Paragraph>
      Demo software from Rocket Pricing is not a finished product, nor should any Demo software be considered supported or maintained by Rocket Pricing, or any other party. Certain Demo software, in part or in whole, may be included under separate agreement, under a separate accompanying end user license agreement, or separate statement of work, in which case the terms and conditions of such agreement shall apply to that Demo software.
    </Paragraph>

    <Paragraph>
      By using Demo software, You are agreeing to be bound by the terms and conditions of this agreement. Do not use Demo software until You have read and agreed to the following terms and conditions. The use of the Demo software implies automatically the acceptance of the following terms and conditions.
    </Paragraph>

    <Paragraph>
      <Text strong>DEMO SOFTWARE STATUS.</Text> Demo software offers limited features allowing You only to evaluate and review the software. Demo software is not an end product (or finished product), nor is it intended or authorized by Rocket Pricing to be integrated into end products. You are not authorized to use the Demo software in any production system, proprietary system, or derivative works, and may not be offered for sale or lease, or sold, leased or otherwise distributed.
    </Paragraph>

    <Paragraph>
      <Text strong>OWNERSHIP AND COPYRIGHT.</Text> Title to the Demo software, related documentation and all copies thereof remain with Rocket Pricing and/or its licensors. You may not remove the copyrights notices from the Demo software. You may not copy the software for back-up or archival purposes. You agree to prevent any unauthorized copying of the Demo software and related documentation.
    </Paragraph>

    <Paragraph>
      <Text strong>RESTRICTIONS.</Text> You may not sell, assign, sublicense or otherwise distribute the Demo software for commercial purposes, in whole or in part, or use Demo software in a production system. Except as provided in this Agreement or in the Demo software's documentation, You may not reproduce the Demo software or related documentation, or modify, reverse engineer, de-compile or disassemble the Demo software, in whole or in part.
    </Paragraph>

    <Paragraph>
      <Text strong>NO WARRANTY.</Text> THE DEMO SOFTWARE IS PROVIDED "AS IS" AND "WITH ALL FAULTS;" WITHOUT WARRANTY OF ANY KIND, EXPRESSED OR IMPLIED. ROCKET PRICING EXPRESSLY DISCLAIMS ALL WARRANTIES, EXPRESSED, IMPLIED OR OTHERWISE, INCLUDING WITHOUT LIMITATION, WARRANTY OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NON-INFRINGEMENT OF INTELLECTUAL PROPERTY RIGHTS. Rocket Pricing does not warrant that the use in whole or in part of the Demo software will be interrupted or error free, will meet any specific requirements, or will operate with the combination of hardware and software selected by You. You are responsible for determining whether the Demo software will be suitable for your intended use or application or will achieve your intended results. Demo software is not designed nor authorized for use in business or safety critical applications such as financial services, life supporting devices or systems, or for military or nuclear applications. Rocket Pricing expressly disclaims any responsibility for such usage which shall be made at Your sole risk, even if Rocket Pricing has been informed in writing of such usage. You shall indemnify Rocket Pricing, its officers, employees and affiliates against all claims arising directly or indirectly from Your incorporation of the Demo software in any application or system where failure of the Demo software could lead to financial loss, death or personal injury.
    </Paragraph>

    <Paragraph>
      ROCKET PRICING shall not have any liability in case of damages, losses, claims or actions anyhow caused from combination of the Demo software with another product, software or device.
    </Paragraph>

    <Paragraph>
      Rocket Pricing has not authorized anyone to make any representation or warranty for the Demo software, and any technical, applications or design information or advice, quality characterization, reliability data or other services provided by Rocket Pricing shall not constitute any representation or warranty by Rocket Pricing or alter this disclaimer or warranty, and no additional obligations or liabilities shall arise from Rocket Pricing's providing such information or services. Rocket Pricing does not assume or authorize any other person to assume for it any other liability in connection with its Demo software.
    </Paragraph>

    <Paragraph>
      All other warranties, conditions or other terms implied by law are excluded to the fullest extent permitted by law.
    </Paragraph>

    <Paragraph>
      <Text strong>LIMITATION OF LIABILITIES.</Text> IN NO EVENT WILL ROCKET PRICING OR ITS LICENSORS BE LIABLE TO YOU OR ANY THIRD PARTY FOR ANY INDIRECT, SPECIAL, CONSEQUENTIAL, INCIDENTAL, PUNITIVE DAMAGES OR OTHER DAMAGES (INCLUDING BUT NOT LIMITED TO, THE COST OF LABOR, REQUALIFICATION, DELAY, LOSS OF PROFITS, LOSS OF REVENUES, LOSS OF DATA, COSTS OF PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES OR THE LIKE) WHETHER BASED ON CONTRACT, TORT, OR ANY OTHER LEGAL THEORY, RELATING TO OR IN CONNECTION WITH THE DEMO PRODUCT, THE DOCUMENTATION OR THIS AGREEMENT, EVEN IF ROCKET PRICING HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
    </Paragraph>

    <Paragraph>
      In no event shall Rocket Pricing's aggregate liability to You or any third party under this agreement for any cause or action, whether based on contract, tort, or any other legal theory, relating to or in connection with the Demo software, the documentation or this agreement, shall exceed the purchase price paid for the Demo software, if any.
    </Paragraph>

    <Paragraph>
      <Text strong>TERMINATION.</Text> Rocket Pricing may terminate this license at any time, if in its sole discretion You are in breach of any of its terms and conditions. Upon termination, Your access to any Demo software will be revoked, or You will immediately destroy or return all copies of the demo software and documentation to Rocket Pricing.
    </Paragraph>

    <Paragraph>
      <Text strong>APPLICABLE LAW AND JURISDICTION.</Text> In case of dispute and in the absence of an amicable settlement, the only competent jurisdiction shall be the state or federal courts located in Cuyahoga County, Ohio, United States of America. The applicable law shall be the law of the State of Ohio.
    </Paragraph>

    <Paragraph>
      <Text strong>SEVERABILITY.</Text> If any provision of this agreement is or becomes, at any time or for any reason, unenforceable or invalid, no other provision of this agreement shall be affected thereby, and the remaining provisions of this agreement shall continue with the same force and effect as if such unenforceable or invalid provisions had not been inserted in this Agreement.
    </Paragraph>

    <Paragraph>
      <Text strong>WAIVER.</Text> The waiver by either party of any breach of any provisions of this Agreement shall not be construed as a waiver of any other or a subsequent breach of the same or a different provision.
    </Paragraph>

    <Paragraph>
      <Text strong>RELATIONSHIP OF THE PARTIES.</Text> Nothing in this Agreement shall create, or be deemed to create, a partnership or the relationship of principal and agent or employer and employee between the Parties. Neither Party has the authority or power to bind, to contract in the name of or to create a liability for the other in any way or for any purpose.
    </Paragraph>
  </Typography>
)

/**
 * Props for EulaModal component
 */
export interface EulaModalProps {
  open: boolean
  onAccept: () => void
  onDecline?: () => void
  /** User's previously accepted EULA version (null if never accepted) */
  previouslyAcceptedVersion?: number | null
}

/**
 * EulaModal Component
 *
 * Blocking modal for EULA acceptance (clickwrap agreement).
 * User must check checkbox and click Accept to proceed.
 * Cannot be dismissed without accepting.
 *
 * Legal requirements:
 * - Full EULA text visible before acceptance
 * - Requires explicit action (checkbox + button)
 * - Acceptance logged with timestamp, IP, version
 */
export const EulaModal: React.FC<EulaModalProps> = ({
  open,
  onAccept,
  onDecline,
  previouslyAcceptedVersion
}) => {
  const { message } = App.useApp()
  // Determine if this is a returning user with an outdated EULA version
  const isReturningUser = previouslyAcceptedVersion !== null && previouslyAcceptedVersion !== undefined
  const [hasAgreed, setHasAgreed] = useState(false)
  const [accepting, setAccepting] = useState(false)

  // Reset checkbox when modal opens/closes
  useEffect(() => {
    if (!open) {
      setHasAgreed(false)
    }
  }, [open])

  const handleCheckboxChange = (e: CheckboxChangeEvent) => {
    setHasAgreed(e.target.checked)
  }

  const handleAccept = async () => {
    setAccepting(true)
    try {
      // Call backend to record acceptance
      const result = await authService.acceptEula(CURRENT_EULA_VERSION)

      if (!result.success) {
        message.error('Failed to record EULA acceptance. Please try again.')
        setAccepting(false)
        return
      }

      // Refresh tokens to get updated claims with new EULA version
      const refreshResult = await authService.refreshTokens()

      if (!refreshResult.success) {
        // EULA was accepted but token refresh failed
        // User may need to re-login to get updated token
        message.warning('EULA accepted. Please refresh the page to continue.')
      }

      message.success('Thank you for accepting the agreement.')
      onAccept()
    } catch (error) {
      console.error('Failed to accept EULA:', error)
      message.error('An error occurred. Please try again.')
    } finally {
      setAccepting(false)
    }
  }

  const handleDecline = () => {
    if (onDecline) {
      onDecline()
    } else {
      // Default behavior: sign out
      authService.signOut()
      window.location.href = '/login'
    }
  }

  const handleDownload = () => {
    // Create a blob with the EULA text and download it
    const blob = new Blob([EULA_PLAIN_TEXT], { type: 'text/plain' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `RocketPricing_EULA_v${CURRENT_EULA_VERSION}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  return (
    <Modal
      open={open}
      title={
        <Space>
          <FileTextOutlined style={{ color: '#1890ff' }} />
          <span>Software License Agreement</span>
        </Space>
      }
      closable={false}
      maskClosable={false}
      keyboard={false}
      width={700}
      centered
      footer={null}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Legal requirement notice - different text for first-time vs returning users */}
        <Alert
          message={isReturningUser ? "Terms Updated" : "Review Required"}
          description={
            isReturningUser
              ? "We've made updates to our agreement. A quick review and acceptance will get you back to work."
              : "Before using Rocket Pricing, please review and accept our Software License Agreement."
          }
          type="info"
          showIcon
        />

        {/* Version info */}
        <Text type="secondary" style={{ fontSize: '12px' }}>
          Version {CURRENT_EULA_VERSION}
        </Text>

        {/* Scrollable EULA text */}
        <div
          style={{
            maxHeight: '400px',
            overflowY: 'auto',
            padding: '16px',
            border: '1px solid #d9d9d9',
            borderRadius: '4px',
            backgroundColor: '#fafafa'
          }}
          role="region"
          aria-label="License agreement text"
        >
          <EulaContent />
        </div>

        <Divider style={{ margin: '12px 0' }} />

        {/* Agreement checkbox */}
        <Checkbox
          checked={hasAgreed}
          onChange={handleCheckboxChange}
          style={{ fontSize: '16px' }}
          aria-required="true"
        >
          <Text strong>I have read and agree to the Software License Agreement</Text>
        </Checkbox>

        {/* Action buttons */}
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <div>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownload}
              type="link"
            >
              Download Text
            </Button>
            <Button
              onClick={handleDecline}
              type="text"
              danger
            >
              Decline & Logout
            </Button>
          </div>
          <Button
            type="primary"
            size="large"
            icon={<CheckCircleOutlined />}
            onClick={handleAccept}
            disabled={!hasAgreed}
            loading={accepting}
          >
            Accept Agreement
          </Button>
        </Space>
      </Space>
    </Modal>
  )
}

/**
 * Check if user needs to accept EULA
 * @param acceptedVersion - User's currently accepted EULA version (from /me endpoint)
 * @returns true if user needs to accept current EULA version
 */
export const needsEulaAcceptance = (acceptedVersion: number | null | undefined): boolean => {
  if (acceptedVersion === null || acceptedVersion === undefined) {
    return true // Never accepted
  }
  return acceptedVersion < CURRENT_EULA_VERSION
}

export default EulaModal
